/**
 * "Einstellungen": seit Migration 0046 für JEDEN eingeloggten Benutzer
 * erreichbar (siehe App.tsx/Sidebar.tsx/MobileDrawer.tsx - kein
 * hasPermission()-Gate mehr auf den Menüpunkt selbst). Zwei unabhängige
 * Bereiche:
 *   - "Meine Reiter": rein persönliche Anzeige-Präferenz, JEDER Benutzer
 *     kann per Schiebregler einzelne Reiter der eigenen Navigation
 *     ausblenden (users.hidden_nav_items, AuthContext.isNavItemHidden()/
 *     setHiddenNavItems()). "Einstellungen" selbst erscheint hier bewusst
 *     NICHT in der Liste - dieser Reiter ist als einziger nicht ausblendbar
 *     (serverseitig zusätzlich erzwungen, siehe backend/src/routes/auth.ts).
 *     Angezeigt werden nur Reiter, die der Benutzer aktuell überhaupt sehen
 *     dürfte (Berechtigung vorhanden) - ein Reiter ohne Berechtigung taucht
 *     hier gar nicht erst auf.
 *   - "Allgemein": die bisherigen globalen Systemeinstellungen, weiterhin
 *     ausschließlich mit der Berechtigung "settings.manage" sicht- und
 *     nutzbar (Backend PUT /api/settings/:key bleibt unverändert
 *     entsprechend abgesichert).
 */
import { useEffect, useState } from "react";
import { api, ApiError } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { Card, CardHeader } from "../../components/ui/Card";
import { Spinner } from "../../components/ui/Spinner";
import { Toggle } from "../../components/ui/Toggle";
import { NAV_ITEMS } from "../../components/layout/navItems";

interface SettingRow { key: string; value: string; updated_at: string }

const LABELS: Record<string, string> = {
  company_name: "Firmenname",
  currency: "Währung",
  sale_number_prefix: "Präfix für Verkaufsnummern",
  sale_number_counter: "Verkaufsnummern-Zähler",
  low_stock_alert_enabled: "Warnung bei niedrigem Bestand aktiv",
};

/** Spiegelt die hartkodierten Navigationsblöcke aus Sidebar.tsx/
 *  MobileDrawer.tsx (Reiter außerhalb von NAV_ITEMS) - ausschließlich für
 *  die Sichtbarkeits-Prüfung dieser Liste, keine eigene Quelle der
 *  Navigations-Darstellung selbst. */
const HARD_CODED_ITEMS: { to: string; label: string; icon: string; isVisible: (hasPermission: (p: string) => boolean, isSuperAdmin: boolean) => boolean }[] = [
  { to: "/warehouse-sync", label: "Lagersystem", icon: "🔄", isVisible: (hasPermission) => hasPermission("discord_sync.view") },
  { to: "/kassensystem", label: "Kassensystem", icon: "💵", isVisible: (hasPermission) => hasPermission("kassensystem.view") },
  { to: "/sanctionssystem", label: "Sanktionssystem", icon: "🚨", isVisible: (hasPermission) => hasPermission("sanctionssystem.view") },
  { to: "/route", label: "Benzin-Route", icon: "🗺️", isVisible: (hasPermission) => hasPermission("route.view") },
  { to: "/route-rules", label: "Routenregeln", icon: "📖", isVisible: (hasPermission) => hasPermission("routenregeln.view") },
  { to: "/messages", label: "Nachrichten", icon: "✉️", isVisible: (hasPermission) => hasPermission("messages.manage") },
  { to: "/blacklist", label: "Blacklist", icon: "🚫", isVisible: (hasPermission) => hasPermission("blacklist.manage") },
  { to: "/price-list", label: "Preisliste", icon: "🏷️", isVisible: (hasPermission) => hasPermission("price_list.view") },
  { to: "/das-ende", label: "Das Ende", icon: "💀", isVisible: (_hasPermission, isSuperAdmin) => isSuperAdmin },
  { to: "/change-password", label: "Passwort ändern", icon: "🔑", isVisible: () => true },
];

const HIDABLE_ITEMS = [
  ...NAV_ITEMS.map((item) => ({
    to: item.to, label: item.label, icon: item.icon,
    isVisible: (hasPermission: (p: string) => boolean) => hasPermission(item.permission),
  })),
  ...HARD_CODED_ITEMS,
];

export default function Settings() {
  const { user, hasPermission, isNavItemHidden, setHiddenNavItems } = useAuth();
  const canManageGlobalSettings = hasPermission("settings.manage");

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-xl font-semibold text-gray-100">Einstellungen</h1>

      <MyTabsCard
        hasPermission={hasPermission}
        isSuperAdmin={!!user?.isSuperAdmin}
        isNavItemHidden={isNavItemHidden}
        setHiddenNavItems={setHiddenNavItems}
        hiddenNavItems={user?.hiddenNavItems ?? []}
      />

      {canManageGlobalSettings && <GlobalSettingsCard />}
    </div>
  );
}

function MyTabsCard({
  hasPermission, isSuperAdmin, isNavItemHidden, setHiddenNavItems, hiddenNavItems,
}: {
  hasPermission: (p: string) => boolean;
  isSuperAdmin: boolean;
  isNavItemHidden: (to: string) => boolean;
  setHiddenNavItems: (items: string[]) => Promise<void>;
  hiddenNavItems: string[];
}) {
  const [savingTo, setSavingTo] = useState<string | null>(null);
  const items = HIDABLE_ITEMS.filter((item) => item.isVisible(hasPermission, isSuperAdmin));

  async function toggle(to: string, nextVisible: boolean) {
    setSavingTo(to);
    try {
      const next = nextVisible ? hiddenNavItems.filter((t) => t !== to) : [...hiddenNavItems, to];
      await setHiddenNavItems(next);
    } finally {
      setSavingTo(null);
    }
  }

  return (
    <Card>
      <CardHeader title="Meine Reiter" subtitle="Blende einzelne Reiter in deiner eigenen Navigation aus - nur für dich, jederzeit wieder einblendbar." />
      <div className="space-y-1">
        {items.map((item) => {
          const visible = !isNavItemHidden(item.to);
          return (
            <div key={item.to} className="flex items-center justify-between gap-3 py-2 border-b border-surface-border/60 last:border-b-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="text-base">{item.icon}</span>
                <span className="text-sm text-gray-200 truncate">{item.label}</span>
              </div>
              <Toggle checked={visible} disabled={savingTo === item.to} onChange={(next) => toggle(item.to, next)} />
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function GlobalSettingsCard() {
  const [settings, setSettings] = useState<SettingRow[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  function load() {
    setLoading(true);
    api.get<SettingRow[]>("/api/settings").then((rows) => {
      setSettings(rows);
      setValues(Object.fromEntries(rows.map((r) => [r.key, r.value])));
    }).finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function save(key: string) {
    setSavingKey(key);
    setMessage(null);
    try {
      await api.put(`/api/settings/${key}`, { value: values[key] });
      setMessage(`"${LABELS[key] ?? key}" gespeichert.`);
      load();
    } catch (err) {
      setMessage(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSavingKey(null);
    }
  }

  if (loading) return <Spinner />;

  return (
    <>
      {message && <div className="bg-accent-muted text-accent text-sm rounded-lg px-3 py-2">{message}</div>}

      <Card>
        <CardHeader title="Allgemein" subtitle="Globale Einstellungen für die gesamte Anwendung" />
        <div className="space-y-4">
          {settings.filter((s) => s.key !== "sale_number_counter").map((s) => (
            <div key={s.key} className="flex items-end gap-3">
              <div className="flex-1">
                <label className="label">{LABELS[s.key] ?? s.key}</label>
                <input
                  className="input"
                  value={values[s.key] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [s.key]: e.target.value }))}
                />
              </div>
              <button className="btn-secondary" onClick={() => save(s.key)} disabled={savingKey === s.key}>
                {savingKey === s.key ? "…" : "Speichern"}
              </button>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}
